/**
 * Name Resolver - 서버가 보내는 name_id / label 형식의 코드를
 * 사람이 읽을 수 있는 한글 표시명으로 변환한다.
 *
 * L1J 3.63C 서버는 NPC/몬스터/아이템의 표시명을 다음과 같은 코드로 전송한다:
 *   - NPC: `$958` 같은 name_id (DB `npcs.name` 컬럼의 한글 이름을 별도 매핑으로 조회)
 *   - 아이템: `($123)` 같은 라벨 (괄호 포함, ITEM_LABELS로 매핑)
 *
 * 이 모듈은 순수 함수로만 구성되어 외부 부수효과가 없다.
 */

import { NPC_NAMES } from '../data/npcNames';
import { ITEM_LABELS } from '../data/itemLabels';
import { UNIDENTIFIED_ITEM_NAMES } from '../data/unidentifiedItemNames';
import { SERVER_MESSAGES } from '../data/serverMessages';

/**
 * NPC/Monster name_id를 한글 표시명으로 변환한다.
 *
 * @param rawName 서버에서 받은 name_id 문자열. 예: `"$958"` (고블린).
 *               `$숫자 $숫자` 같은 결합 키, `$숫자 문자` 같은 변형도 그대로 조회한다.
 * @returns `NPC_NAMES`에서 찾은 한글 이름. 매핑이 없거나 입력이 비어 있으면
 *          입력을 그대로 반환한다. 단, `rawName`이 빈 문자열이면 빈 문자열을 반환한다.
 */
export const resolveNpcName = (rawName: string): string => {
  if (!rawName) return '';
  return NPC_NAMES[rawName] ?? rawName;
};

/**
 * 아이템 name_id를 한글 표시명으로 변환한다.
 *
 * 현재 L1J 서버는 아이템 표시에도 `$숫자` name_id 형식을 사용할 수 있으므로
 * `resolveNpcName`과 동일한 로직을 사용한다. 향후 별도 아이템 매핑 테이블이
 * 추가되면 이 함수만 교체하면 된다.
 *
 * @param rawName 서버에서 받은 아이템 name_id (예: `"$958"`).
 * @returns 매핑된 한글 이름. 매핑이 없거나 입력이 비어 있으면 입력을 그대로 반환한다.
 */
export const resolveItemName = (rawName: string): string => {
  if (!rawName) return '';

  // 문자열 전체가 키인 경우가 먼저다 — `$1958 $1938`처럼 공백이 낀 복합 키가 있어
  // 토큰 단위로 먼저 바꾸면 원래 뜻을 잃는다.
  const exact = UNIDENTIFIED_ITEM_NAMES[rawName] ?? NPC_NAMES[rawName];
  if (exact) return exact;

  // `$8586 (1000)`처럼 키 뒤에 개수·라벨이 붙는 형태 — 키 부분만 바꾸고 나머지는 둔다.
  return rawName.replace(
    /\$\d+/g,
    (token) => UNIDENTIFIED_ITEM_NAMES[token] ?? NPC_NAMES[token] ?? token,
  );
};

/**
 * 아이템 표시명 꼬리의 개수 표기를 떼어 낸다 (`"물약 (7)"` → `"물약"`).
 *
 * 겹치는 아이템은 서버가 이름 자체에 개수를 박아 보낸다
 * (`S_AddItem`의 `getNumberedViewName()`). 그 숫자는 패킷을 만든 시점의
 * 스냅샷이라 개수만 바뀌는 `item_amount`가 오면 뒤처지고, **같은 아이템인데
 * 이름이 달라진다.** 그래서 이름으로 아이템을 견주는 곳(퀵슬롯 재바인딩 등)은
 * 반드시 이 함수를 거친 이름을 써야 한다. 개수의 진실은 `count` 필드뿐이다.
 *
 * `$숫자` 리소스 키 뒤에 붙은 경우도 있으므로 `resolveItemName()`보다
 * **먼저** 떼야 `"$3301 (9)"`가 `"$3301"`로 매핑에 걸린다.
 */
export const stripCountSuffix = (rawName: string): string =>
  (rawName ?? '').replace(/\s*\(\d+\)\s*$/, '');

/**
 * `($숫자)` 형식(괄호 포함) 아이템 상태 라벨을 한글 라벨로 변환한다.
 *
 * ITEM_LABELS는 `web-client/src/data/itemLabels.ts`에서 export 되며,
 * 키 형식은 `($123)` 같이 괄호가 포함된 문자열이다 (예: `"(점등중)"`,
 * `"(장비)"`, `"(보호중)"`). 매핑이 없으면 원본 라벨을 그대로 반환한다.
 *
 * @param rawLabel 서버에서 받은 라벨 문자열. 예: `"($9)"`.
 * @returns ITEM_LABELS에서 찾은 한글 라벨. 매핑이 없거나 입력이 비어 있으면
 *          입력을 그대로 반환한다.
 */
export const resolveItemLabel = (rawLabel: string): string => {
  if (!rawLabel) return '';
  return ITEM_LABELS[rawLabel] ?? rawLabel;
};

/**
 * 주어진 문자열이 name_id 패턴(`$숫자`)인지 확인한다.
 *
 * 결합된 키(`$1242 $1044`)나 접미사가 붙은 키(`$7901 아줄`)는
 * 여기서 `false`를 반환한다 — 그런 키는 `NPC_NAMES`에서 결합 키로 직접 조회해야 한다.
 *
 * @param s 검사할 문자열.
 * @returns `$숫자` 패턴이면 `true`, 아니면 `false`.
 */
export const isLikelyNameId = (s: string): boolean => /^\$\d+$/.test(s);

/** `($9)`처럼 괄호까지 포함된 상태 라벨. 이름 ID 치환보다 먼저 걷어내야 한다. */
const LABEL_PATTERN = /\(\$\d+\)/g;

/**
 * `$1958 $1938`(두 토큰 결합 키) 또는 `$958`(단일 키).
 * 결합 키가 먼저 매칭되도록 두 토큰 형태를 앞에 둔다 — 토큰 단위로 먼저 바꾸면
 * `$1958 $1938`(양손검)이 `$1958`+`$1938`로 쪼개져 원래 뜻을 잃는다.
 */
const NAME_ID_PATTERN = /\$\d+(?:\s+\$\d+)?/g;

const lookupNameId = (key: string): string | undefined =>
  UNIDENTIFIED_ITEM_NAMES[key] ?? NPC_NAMES[key];

/**
 * S_ServerMessage의 인자 하나를 사람이 읽을 수 있는 문자열로 바꾼다.
 *
 * 서버는 인자에 아이템/NPC의 이름 ID를 그대로 실어 보낸다. 형태가 제각각이라
 * (`"$958"`, `"$1958 $1938"`, `"+3 $1958 $1938 ($9)"`, `"고블린"`) 다음 순서로 처리한다:
 *
 *   1. 문자열 전체가 매핑 키인 경우 — 결합 키를 쪼개지 않고 통째로 조회한다.
 *   2. `($숫자)` 상태 라벨을 ITEM_LABELS로 치환한다.
 *   3. 남은 이름 ID를 결합 키 우선으로 치환한다.
 *
 * 매핑에 없는 토큰은 원문 그대로 남는다 — 무엇이 왔는지 추적할 수 있어야 한다.
 *
 * @param arg 서버에서 받은 인자 문자열.
 * @returns 이름 ID가 치환된 문자열. 입력이 비어 있으면 빈 문자열.
 */
export const resolveMessageArg = (arg: string): string => {
  if (!arg) return '';

  const exact = lookupNameId(arg);
  if (exact) return exact;

  return arg
    .replace(LABEL_PATTERN, (label) => ITEM_LABELS[label] ?? label)
    .replace(NAME_ID_PATTERN, (match) => {
      const joined = match.replace(/\s+/g, ' ');
      const pair = lookupNameId(joined);
      if (pair) return pair;

      // 결합 키로는 못 찾았으니 토큰 하나씩 다시 시도한다.
      return joined
        .split(' ')
        .map((token) => lookupNameId(token) ?? token)
        .join(' ');
    });
};

/* ======================== NPC 이름 / 칭호 ======================== */

/**
 * L1J의 NPC 이름은 `"이름^칭호"` 한 줄에 두 값을 담는다
 * (예: `"물약 상인^키리스"`, `"초보자 가이드^초보자 안내 인"`).
 *
 * DB 실측(`npcs` 3480행): `^`가 든 행은 159개, 그중
 *   - `^`가 두 개 이상인 행 0개
 *   - `^` 앞뒤에 공백이 붙은 행 0개
 *   - `^`로 시작하거나 끝나는 행 0개
 * 이므로 **첫 `^` 하나로 가르는** 단순 규칙으로 충분하다. 그래도 데이터가
 * 늘어날 수 있으니 양쪽 공백은 떼고, 칭호가 비면 없는 것으로 취급한다.
 *
 * 이 규칙은 `scripts/gen_npc_gfx_names.py`의 분리 규칙과 **반드시 같아야 한다** —
 * 어긋나면 같은 NPC가 (표에서 온 이름인지 패킷에서 온 이름인지에 따라)
 * 화면에서 다르게 보인다.
 */
const NPC_TITLE_SEPARATOR = '^';

/**
 * `"이름^칭호"` 를 갈라 표시용 이름만 돌려준다. `^`가 없으면 원문 그대로.
 *
 * @param name 서버/DB에서 온 NPC 이름.
 * @returns `^` 앞부분(공백 제거). 입력이 비어 있으면 빈 문자열.
 */
export function stripNpcTitle(name: string): string {
  return splitNpcName(name).name;
}

/**
 * `"이름^칭호"` → `{ name, title }`. 칭호가 없으면 `title`은 `null`.
 *
 * @param name 서버/DB에서 온 NPC 이름.
 * @returns 이름과 칭호. 입력이 비어 있으면 `{ name: '', title: null }`.
 */
export function splitNpcName(name: string): { name: string; title: string | null } {
  if (!name) return { name: '', title: null };

  const sep = name.indexOf(NPC_TITLE_SEPARATOR);
  if (sep < 0) return { name: name.trim(), title: null };

  const head = name.slice(0, sep).trim();
  const tail = name.slice(sep + 1).trim();
  return { name: head, title: tail || null };
}

/* ======================== 한국어 조사 ======================== */

/**
 * 조사 표기 → `[받침 있을 때, 받침 없을 때]`.
 *
 * 실제로 쓰이는 표기만 넣는다. `src/data/serverMessages.ts`를 훑으면
 * `이(가)` 12건, `을(를)` 7건, `은(는)` 2건, `과(와)` 1건이 나온다
 * (`으로(로)`는 현재 0건). 표기 순서가 뒤집힌 형태(`가(이)` 등)는 데이터에
 * 없지만, 어느 쪽을 앞에 적을지는 문장을 쓴 사람 마음이라 양쪽 다 받아 둔다.
 *
 * `과(와)`처럼 **앞에 적힌 쪽이 받침 있을 때 쓰는 형태**라는 규칙이 4종 모두
 * 일관되므로 값은 항상 `[받침O, 받침X]` 순서로 적는다.
 */
const PARTICLE_FORMS: Readonly<Record<string, readonly [string, string]>> = {
  '은(는)': ['은', '는'],
  '는(은)': ['은', '는'],
  '을(를)': ['을', '를'],
  '를(을)': ['을', '를'],
  '이(가)': ['이', '가'],
  '가(이)': ['이', '가'],
  '과(와)': ['과', '와'],
  '와(과)': ['과', '와'],
  '으로(로)': ['으로', '로'],
  '로(으로)': ['으로', '로'],
};

/** 앞의 공백까지 함께 먹는다 — 고른 조사는 앞말에 붙여 써야 자연스럽다. */
const PARTICLE_PATTERN = new RegExp(
  `\\s*(${Object.keys(PARTICLE_FORMS)
    .map((form) => form.replace(/[()]/g, '\\$&'))
    .join('|')})`,
  'g',
);

/** 한글 음절 영역. 자모(ㄱ, ㅏ)나 옛한글은 대상이 아니다. */
const HANGUL_FIRST = 0xac00;
const HANGUL_LAST = 0xd7a3;

/**
 * 숫자를 한국어로 읽었을 때의 끝소리. 서버 메시지에 `'PK 횟수가 %0 이(가)
 * 되었습니다.'`처럼 **숫자가 조사 앞에 오는 문장**이 실제로 있어서 필요하다.
 * 값은 `[받침 있음, 그 받침이 ㄹ인가]`.
 * (영 0 / 일 1 / 이 2 / 삼 3 / 사 4 / 오 5 / 육 6 / 칠 7 / 팔 8 / 구 9)
 */
const DIGIT_ENDINGS: Readonly<Record<string, readonly [boolean, boolean]>> = {
  '0': [true, false], // 영 — ㅇ
  '1': [true, true], //  일 — ㄹ
  '2': [false, false], // 이
  '3': [true, false], // 삼 — ㅁ
  '4': [false, false], // 사
  '5': [false, false], // 오
  '6': [true, false], // 육 — ㄱ
  '7': [true, true], //  칠 — ㄹ
  '8': [true, true], //  팔 — ㄹ
  '9': [false, false], // 구
};

/** 괄호류 — 끝소리를 볼 때 통째로 건너뛴다. */
const BRACKET_PAIRS: Readonly<Record<string, string>> = {
  ')': '(',
  ']': '[',
  '}': '{',
  '>': '<',
  '】': '【',
  '』': '『',
};

/**
 * 조사 바로 앞의 끝소리를 판정한다.
 *
 * 서버 메시지의 인자는 `"기타 아이템 (40)"`(개수 표기)나 `"+3 장검 (장비)"`(상태
 * 라벨)처럼 **괄호로 끝나는 경우가 흔하다.** 괄호 안은 읽지 않고 넘기는 게
 * 사람이 읽는 방식이므로("기타 아이템(40)을"은 '아이템'에 맞춘다), 닫는 괄호를
 * 만나면 짝을 찾아 통째로 건너뛰고 그 앞을 다시 본다.
 *
 * @returns `[받침 있음, 받침이 ㄹ]`. 판정할 수 없으면 `null`.
 */
function readEnding(text: string): readonly [boolean, boolean] | null {
  let end = text.length;

  for (;;) {
    // 공백·따옴표 같은 장식은 소리가 없으니 지나친다.
    while (end > 0 && /[\s"'`«»“”‘’]/.test(text[end - 1])) end -= 1;
    if (end === 0) return null;

    const last = text[end - 1];
    const open = BRACKET_PAIRS[last];
    if (!open) break;

    // 짝이 되는 여는 괄호를 뒤에서부터 찾는다. 짝이 없으면 판정 포기.
    let depth = 0;
    let i = end - 1;
    for (; i >= 0; i -= 1) {
      if (text[i] === last) depth += 1;
      else if (text[i] === open) {
        depth -= 1;
        if (depth === 0) break;
      }
    }
    if (i < 0) return null;
    end = i;
  }

  const ch = text[end - 1];
  const code = ch.charCodeAt(0);

  if (code >= HANGUL_FIRST && code <= HANGUL_LAST) {
    const jong = (code - HANGUL_FIRST) % 28;
    return [jong !== 0, jong === 8]; // 8 = ㄹ
  }

  const digit = DIGIT_ENDINGS[ch];
  if (digit) return digit;

  // 영문·기호 등은 읽는 방법이 사람마다 달라 확실히 틀릴 수 있다.
  // 잘못 고르느니 `이(가)` 원문을 그대로 남겨 둔다 — 원본 클라이언트도 그렇게 보인다.
  return null;
}

/**
 * 한국어 조사 자동 선택. `"이(가)"` `"을(를)"` `"은(는)"` `"와(과)"` `"으로(로)"`
 * 형태를 앞 단어에 맞춰 고른다.
 *
 * 규칙:
 *   - 받침 판정은 한글 음절 코드로 한다 — `(code - 0xAC00) % 28 !== 0`이면 받침 있음.
 *   - 앞말이 괄호로 끝나면 괄호 안을 건너뛰고 그 앞 글자를 본다
 *     (`"기타 아이템 (40) 을(를)"` → '템'으로 판정 → `"기타 아이템 (40)을"`).
 *   - 앞말이 숫자면 한국어 읽기의 끝소리를 쓴다 (`DIGIT_ENDINGS`).
 *   - 앞말이 영문·기호이거나 아예 없으면 **원문을 그대로 둔다.** 추측해서 틀리면
 *     문장이 어색해지지만, `이(가)` 표기는 원본 클라이언트에서도 보이던 모양이라
 *     그대로 두는 편이 안전하다.
 *   - `으로/로`는 ㄹ 받침 뒤에서 `로`가 된다 ('칼로', '1(일)로').
 *   - 조사 앞의 공백은 함께 먹는다 — 고른 조사는 앞말에 붙여 쓴다.
 *
 * 순수 함수다. 판정은 항상 **원본 문자열**을 보고 하므로 같은 입력이면 같은 출력이다.
 *
 * @param text 조사 표기가 섞인 문장.
 * @returns 조사가 정리된 문장. 입력이 비어 있으면 빈 문자열.
 */
export function fixKoreanParticles(text: string): string {
  if (!text) return '';

  return text.replace(PARTICLE_PATTERN, (match: string, form: string, offset: number) => {
    const ending = readEnding(text.slice(0, offset));
    if (!ending) return match;

    const [hasCoda, isRieul] = ending;
    const [withCoda, withoutCoda] = PARTICLE_FORMS[form];

    // 으로/로만 예외 — ㄹ 받침 뒤에서는 받침이 없는 것처럼 '로'를 쓴다.
    return hasCoda && !(isRieul && withCoda === '으로') ? withCoda : withoutCoda;
  });
}

/**
 * S_ServerMessage(opcode 14)를 화면에 뿌릴 한 줄로 만든다.
 *
 * `data/serverMessages.ts`의 동명 함수와 달리 인자에 섞인 `$숫자` 이름 ID까지
 * 실제 이름으로 바꾼다. 채팅/시스템 메시지 출력은 이쪽을 쓰면 된다.
 *
 * 표에 없는 번호는 인자만이라도 이어 붙여 보여주고, 인자마저 없으면 번호를 남긴다.
 *
 * 인자를 끼워 넣은 **뒤에** `fixKoreanParticles()`를 한 번 통과시킨다 — 조사는
 * 바로 앞말의 받침을 봐야 고를 수 있으므로 `%0`이 실제 이름으로 바뀐 다음이라야 한다.
 *
 * @param messageId 서버가 보낸 메시지 번호.
 * @param args 서버가 보낸 인자 배열. 없으면 빈 배열로 취급한다.
 * @returns 화면에 그대로 출력할 수 있는 문자열.
 */
export function formatServerMessage(messageId: number, args: string[] = []): string {
  const resolved = args.map(resolveMessageArg);
  const template = SERVER_MESSAGES[messageId];

  if (!template) {
    const joined = resolved.filter(Boolean).join(' ').trim();
    return fixKoreanParticles(joined) || `서버 메시지 #${messageId}`;
  }

  return fixKoreanParticles(
    template.replace(/%(\d)/g, (_, i: string) => resolved[Number(i)] ?? ''),
  );
}